"use client";

import { useState } from "react";
import { cn } from "../../lib/utils";
import { formatPrice } from "./utils";
import { AspectRatio } from "../../components/aspect-ratio";

// Preset amounts in pence (£50, £100, £150)
const PRESET_AMOUNTS = [
  { value: 5000, label: "£50" },
  { value: 10000, label: "£100" },
  { value: 15000, label: "£150" },
  { value: 0, label: "Custom" },
] as const;

export type GiftcardDetailsValues = {
  /** Amount in pence/cents (e.g., 5000 = £50.00) */
  amount: number;
  /** Custom amount input string (for custom option) */
  customAmount: string;
  /** Recipient name (To field) */
  recipientName: string;
  /** Sender name (From field) */
  senderName: string;
  /** Message to include with the gift card */
  message: string;
  /** Recipient email address */
  recipientEmail: string;
  /** Selected gift card image URL */
  selectedImage?: string;
};

type GiftcardDetailsStepProps = {
  values: GiftcardDetailsValues;
  currency?: string;
  /** Available gift card images to choose from */
  images?: string[];
  onChange: (field: keyof GiftcardDetailsValues, value: string | number) => void;
  onBlur?: (field: keyof GiftcardDetailsValues) => void;
  errors?: Partial<Record<keyof GiftcardDetailsValues, string>>;
  touched?: Partial<Record<keyof GiftcardDetailsValues, boolean>>;
};

export function GiftcardDetailsStep({
  values,
  currency = "GBP",
  images = [],
  onChange,
  onBlur,
  errors = {},
  touched = {},
}: GiftcardDetailsStepProps) {
  const currencySymbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
  const isCustomAmount = !PRESET_AMOUNTS.slice(0, -1).some(p => p.value === values.amount);

  // Find selected image index
  const selectedImageIndex = values.selectedImage
    ? images.findIndex(img => img === values.selectedImage)
    : 0;
  const currentIndex = selectedImageIndex >= 0 ? selectedImageIndex : 0;

  const handleImageSelect = (image: string) => {
    onChange("selectedImage", image);
  };

  const handleAmountSelect = (value: number) => {
    if (value === 0) {
      // Custom option selected - keep current amount or set to empty
      onChange("amount", 0);
      onChange("customAmount", "");
    } else {
      onChange("amount", value);
      onChange("customAmount", "");
    }
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    onChange("customAmount", value);
    // Convert to pence
    const amountInPence = Math.round(parseFloat(value || "0") * 100);
    if (!isNaN(amountInPence) && amountInPence >= 0) {
      onChange("amount", amountInPence);
    }
  };

  return (
    <div className="space-y-6">
      {/* Image Gallery */}
      {images.length > 0 && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">
            Choose a Design
          </label>
          <div className="space-y-3">
            {/* Main Image */}
            <div className="overflow-hidden rounded-xl bg-muted">
              <AspectRatio ratio={3 / 2}>
                <img
                  src={images[currentIndex]}
                  alt="Gift card design"
                  className="h-full w-full object-cover"
                />
              </AspectRatio>
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleImageSelect(image)}
                    className={cn(
                      "relative shrink-0 overflow-hidden rounded-lg transition-all",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      currentIndex === index
                        ? "ring-2 ring-primary ring-offset-2"
                        : "opacity-60 hover:opacity-100"
                    )}
                  >
                    <img
                      src={image}
                      alt={`Design option ${index + 1}`}
                      className="h-14 w-20 object-cover sm:h-16 sm:w-24"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Image count indicator */}
            {images.length > 1 && (
              <p className="text-center text-xs text-muted-foreground">
                {currentIndex + 1} of {images.length} designs
              </p>
            )}
          </div>
        </div>
      )}

      {/* Amount Selection */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          Gift Card Amount
        </label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {PRESET_AMOUNTS.map((preset) => {
            const isSelected = preset.value === 0
              ? isCustomAmount
              : values.amount === preset.value;

            return (
              <button
                key={preset.value}
                type="button"
                onClick={() => handleAmountSelect(preset.value)}
                className={cn(
                  "flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all",
                  "text-sm font-medium cursor-pointer",
                  isSelected
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-foreground hover:border-primary/50"
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* Custom Amount Input */}
        {isCustomAmount && (
          <div className="relative mt-3">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {currencySymbol}
            </span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Enter amount (min £5)"
              value={values.customAmount}
              onChange={handleCustomAmountChange}
              onBlur={() => onBlur?.("amount")}
              className={cn(
                "w-full pl-7 pr-4 py-3 rounded-lg border bg-background text-foreground",
                "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
                touched?.amount && errors?.amount
                  ? "border-destructive"
                  : "border-input"
              )}
            />
          </div>
        )}
        {touched?.amount && errors?.amount && (
          <p className="text-sm text-destructive">{errors.amount}</p>
        )}
      </div>

      {/* Recipient Email */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Recipient's Email <span className="text-destructive">*</span>
        </label>
        <input
          type="email"
          placeholder="Where should we send the gift card?"
          value={values.recipientEmail}
          onChange={(e) => onChange("recipientEmail", e.target.value)}
          onBlur={() => onBlur?.("recipientEmail")}
          className={cn(
            "w-full px-4 py-3 rounded-lg border bg-background text-foreground",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
            touched?.recipientEmail && errors?.recipientEmail
              ? "border-destructive"
              : "border-input"
          )}
        />
        {touched?.recipientEmail && errors?.recipientEmail && (
          <p className="text-sm text-destructive">{errors.recipientEmail}</p>
        )}
      </div>

      {/* To Field */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          To <span className="text-destructive">*</span>
        </label>
        <input
          type="text"
          placeholder="Recipient's name"
          value={values.recipientName}
          onChange={(e) => onChange("recipientName", e.target.value)}
          onBlur={() => onBlur?.("recipientName")}
          className={cn(
            "w-full px-4 py-3 rounded-lg border bg-background text-foreground",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
            touched?.recipientName && errors?.recipientName
              ? "border-destructive"
              : "border-input"
          )}
        />
        {touched?.recipientName && errors?.recipientName && (
          <p className="text-sm text-destructive">{errors.recipientName}</p>
        )}
      </div>

      {/* From Field */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          From <span className="text-destructive">*</span>
        </label>
        <input
          type="text"
          placeholder="Your name"
          value={values.senderName}
          onChange={(e) => onChange("senderName", e.target.value)}
          onBlur={() => onBlur?.("senderName")}
          className={cn(
            "w-full px-4 py-3 rounded-lg border bg-background text-foreground",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
            touched?.senderName && errors?.senderName
              ? "border-destructive"
              : "border-input"
          )}
        />
        {touched?.senderName && errors?.senderName && (
          <p className="text-sm text-destructive">{errors.senderName}</p>
        )}
      </div>

      {/* Message Field */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Personal Message <span className="text-muted-foreground">(optional)</span>
        </label>
        <textarea
          placeholder="Add a personal message..."
          value={values.message}
          onChange={(e) => onChange("message", e.target.value)}
          rows={3}
          className={cn(
            "w-full px-4 py-3 rounded-lg border bg-background text-foreground resize-none",
            "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50",
            "border-input"
          )}
        />
      </div>
    </div>
  );
}

